import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { CopilotChannelContext } from './CopilotChannelContext';
import { createCopilotChannel } from './createCopilotChannel';
import { jsx as _jsx } from "react/jsx-runtime";
const CopilotChannelProvider = ({
  children,
  createLayer,
  instanceId
}) => {
  const channel = useMemo(() => createCopilotChannel({
    createLayer,
    instanceId
  }), [createLayer, instanceId]);
  const subscribe = useCallback(onStoreChange => {
    // Because this is stored on the window we have to make this backwards
    // compatible and check that it exists
    if (!channel || typeof channel.addStateListener !== 'function') {
      return () => {};
    }
    const unsubscribe = channel.addStateListener(onStoreChange);
    // The channel is a singleton on the top window, but when this provider is
    // hosted in an iframe (e.g. the Breeze Assistant chat), this listener
    // closure belongs to the iframe realm. If the host app tears the iframe
    // out of the DOM, React never unmounts this provider, so this subscribe's
    // own cleanup never runs and the listener is orphaned on the shared
    // channel — a later setState notify then re-renders into a torn-down realm
    // and crashes the renderer. pagehide fires on iframe teardown, so drop the
    // listener there as well. Ignore a bfcache freeze (persisted === true):
    // the document is frozen then restored on pageshow without a remount, so
    // unsubscribing then would permanently disconnect the restored provider.
    const onPageHide = event => {
      if (!event.persisted) {
        unsubscribe();
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      unsubscribe();
    };
  }, [channel]);
  const getState = useMemo(() => {
    // Because this is stored on the window we have to make this backwards
    // compatible and check that it exists
    if (channel && typeof channel.getState === 'function') {
      return channel.getState;
    }
    const defaultState = {
      appContext: {},
      isCanvasOpen: false,
      isNavCopilotAvailable: false,
      isWidgetOpen: false,
      isWidgetActive: false,
      layoutType: 'panel',
      breezeContext: {
        messageContexts: [],
        context: {},
        turnMetadata: {},
        conversationMetadata: {},
        agentId: undefined
      }
    };
    return () => defaultState;
  }, [channel]);
  const channelState = useSyncExternalStore(subscribe, getState);
  const contextValue = useMemo(() => ({
    channel,
    channelState,
    instanceId
  }), [channel, channelState, instanceId]);
  return /*#__PURE__*/_jsx(CopilotChannelContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default CopilotChannelProvider;